/* eslint  react-hooks/exhaustive-deps: 0 */
import { useEffect, useState } from "react";
import { PageContainer } from "../shared/components/PageContainer";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useFetchContext } from "../../contexts/FetchContext";
import {
  DocumentStatus,
  DocumentsData,
  WarehouseLocation,
} from "../../interfaces/document";
import { Box, Button } from "@mui/material";
import { toast } from "react-toastify";
import DocumentCard from "../documents/components/DocumentCard";
import { ProductData } from "../../interfaces/product";
import ConfirmationModal from "../shared/components/Modal/ConfirmationModal";
import { AxiosError } from "axios";
import SwipingModal from "../shared/components/SwipingModal";
import useLocalStorage from "../../hooks/useLocalStorage";

interface ProductItem {
  serialNumber: string;
  productCode: string;
  warehouseLocation?: string;
}

const Acceptance = () => {
  const { state, fetchData } = useFetchContext();

  const urlParams = new URLSearchParams(window.location.search);

  const uuidParam = urlParams.get("uuid");

  const [tableData, setTableData] = useLocalStorage<Array<ProductItem>>(
    "acceptance-" + uuidParam,
    []
  );
  const [acceptationData, setAcceptationData] = useState<DocumentsData | null>(
    null
  );

  const hasWarehouseLocations =
    acceptationData?.warehouse?.hasWarehouseLocations;
  const warehouseLocations =
    hasWarehouseLocations && acceptationData?.warehouse?.warehouseLocations;

  const [serialNumberValue, setSerialNumberValue] = useState<string>("");
  const [warehouseLocationValue, setWarehouseLocationValue] =
    useState<string>("");

  const [showProductDetails, setShowProductDetails] = useState<string | null>(
    null
  );

  const [hasSerialNumberError, setSerialNumberError] = useState<boolean>(false);
  const [hasWarehouseLocationError, setWarehouseLocationError] =
    useState<boolean>(false);

  const addProductItem = (productItem: ProductItem) => {
    // const alreadyExists = tableData?.find(
    //   (row) => row.serialNumber === productItem.serialNumber
    // );
    setTableData((prevTableData: ProductItem[]) => [
      ...(prevTableData?.filter(
        (p) => p.serialNumber !== productItem.serialNumber
      ) || []),
      productItem,
    ]);
    setSerialNumberValue("");
    setSerialNumberError(false);
    playSound("success");
  };

  const checkSerialNumber = (serialNumber: string) => {
    const product = acceptationData?.products?.find(
      (product) => product.serialNumber === serialNumber
    );
    if (product) {
      if (!hasWarehouseLocations) {
        addProductItem({
          serialNumber,
          productCode: product.productCode,
        });
        setSerialNumberError(false);
        setSerialNumberValue("");
      } else {
        playSound("success");
        document.getElementById("warehouseLocationInput")?.focus();
      }
    } else {
      setSerialNumberError(true);
      setTimeout(() => {
        setSerialNumberError(false);
        setSerialNumberValue("");
      }, 3000);
      playSound("error");
    }
  };

  const checkWarehouseLocation = (warehouseLocation: string) => {
    const location =
      warehouseLocations &&
      warehouseLocations?.find(
        (location: WarehouseLocation) => location.name === warehouseLocation
      );
    if (location) {
      const product = acceptationData?.products?.find(
        (product) => product.serialNumber === serialNumberValue
      );
      if (product) {
        addProductItem({
          serialNumber: serialNumberValue,
          productCode: product?.productCode,
          warehouseLocation,
        });
        playSound("success");
        setWarehouseLocationError(false);
        setSerialNumberError(false);
        setSerialNumberValue("");
        setWarehouseLocationValue("");
        document.getElementById("serialNumberInput")?.focus();
      }
    } else {
      setWarehouseLocationError(true);
      setTimeout(() => {
        setWarehouseLocationError(false);
        setWarehouseLocationValue("");
        document.getElementById("warehouseLocationInput")?.focus();
      }, 3000);
      playSound("error");
    }
  };

  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        const response = await fetchData(`/documents/${uuidParam}`);
        setAcceptationData(response?.data);

        if (response?.data?.status === DocumentStatus.CLOSED) {
          setTableData(
            response?.data?.products?.map((el: ProductData, index: number) => {
              return {
                id: index + 1,
                serialNumber: el.serialNumber,
                productCode: el.productCode,
                warehouseLocation: el.warehouseLocation?.name,
              };
            })
          );
        }
      } catch (error) {}
    };

    fetchDataAsync();
  }, []);

  const cardStyle = {
    marginBottom: "16px",
  };

  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const openConfirmationModal = () => {
    setConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setConfirmationModalOpen(false);
  };

  const playSound = (type: "success" | "error") => {
    let sound = new Howl({
      src: ["/assets/code-correct.wav"],
      volume: 1,
    });
    if (type === "error") {
      sound = new Howl({
        src: ["/assets/code-wrong.wav"],
        volume: 1,
      });
    }
    sound.play();
  };

  const handleConfirmation = async () => {
    try {
      const response = await fetchData(`/documents/close-acceptation`, "POST", {
        documentId: acceptationData?.id,
        products: tableData,
      });

      if (response?.data?.status === DocumentStatus.CLOSED) {
        setAcceptationData(response?.data);
        toast.success("Accettazione completata");
      } else {
        toast.error("Errore con la gestione di questo documento");
      }
      closeConfirmationModal();
    } catch (error) {
      const axiosError = error as AxiosError;
      toast.error(
        axiosError?.message ?? "Errore con la comunicazione con il server"
      );
    }
  };

  const groupProductsByProductCode = () => {
    const productCounts: { [key: string]: number } = {};

    acceptationData?.products.forEach((product) => {
      const { productCode } = product;
      if (productCounts[productCode]) {
        productCounts[productCode]++;
      } else {
        productCounts[productCode] = 1;
      }
    });

    const productCodeQuantities = Object.keys(productCounts).map(
      (productCode) => ({
        productCode,
        quantity: productCounts[productCode],
      })
    );

    return productCodeQuantities;
  };

  const getSerialNumbersTable = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Prodotto</TableCell>
            <TableCell>Caricati</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupProductsByProductCode().map((groupedRow, index) => {
            const matchingItemsCount = tableData?.filter(
              (item) => item.productCode === groupedRow.productCode
            ).length;

            const isMatching = matchingItemsCount === groupedRow.quantity;

            return (
              <TableRow
                key={index}
                sx={isMatching ? { backgroundColor: "primary.light" } : null}
                onClick={() => setShowProductDetails(groupedRow.productCode)}
              >
                <TableCell>{groupedRow.productCode}</TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  {matchingItemsCount} di {groupedRow.quantity}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <PageContainer>
      <Box
        sx={{
          padding: 1,
        }}
      >
        {acceptationData && (
          <DocumentCard elements={acceptationData} sx={cardStyle} />
        )}
        <Card sx={cardStyle}>
          <CardContent sx={{ padding: "15px" }}>
            {acceptationData?.status !== DocumentStatus.CLOSED ? (
              <>
                <TextField
                  autoFocus
                  id="serialNumberInput"
                  label="Inserisci Matricola"
                  value={serialNumberValue}
                  onChange={(e) => {
                    setSerialNumberError(false);
                    setSerialNumberValue(e.target.value);
                  }}
                  onKeyDown={(event: any) => {
                    if (event.key === "Enter") {
                      checkSerialNumber(serialNumberValue);
                      event.preventDefault();
                    }
                  }}
                  sx={{ width: "100%", mb: "15px" }}
                  error={hasSerialNumberError ?? false}
                />
                {hasWarehouseLocations && (
                  <TextField
                    id="warehouseLocationInput"
                    label="Inserisci Ubicazione"
                    value={warehouseLocationValue}
                    onChange={(e) => {
                      setWarehouseLocationError(false);
                      setWarehouseLocationValue(e.target.value);
                    }}
                    onKeyDown={(event: any) => {
                      if (event.key === "Enter") {
                        checkWarehouseLocation(warehouseLocationValue);
                        event.preventDefault();
                      }
                    }}
                    sx={{ width: "100%", mb: "15px" }}
                    error={hasWarehouseLocationError ?? false}
                  />
                )}
                {getSerialNumbersTable()}
              </>
            ) : (
              getSerialNumbersTable()
            )}
          </CardContent>
        </Card>
        {acceptationData?.status !== DocumentStatus.CLOSED && (
          <Box
            sx={{
              bottom: "68px",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={openConfirmationModal}
              disabled={!tableData || tableData.length !== acceptationData?.products?.length}
            >
              ACCETTA
            </Button>
          </Box>
        )}
      </Box>
      <SwipingModal
        title={`MATRICOLE ${showProductDetails ?? ""} (${
          tableData?.filter((p) => p.productCode === showProductDetails)?.length
        }/${acceptationData?.products?.length})`}
        isOpen={showProductDetails !== null}
        content={
          <TableContainer>
            {tableData?.filter((p) => p.productCode === showProductDetails)
              ?.length ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Matricola</TableCell>
                    {hasWarehouseLocations && <TableCell>Ubicazione</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData
                    ?.filter((p) => p.productCode === showProductDetails)
                    ?.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.serialNumber}</TableCell>
                        {hasWarehouseLocations && (
                          <TableCell>{row.warehouseLocation}</TableCell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            ) : (
              ""
            )}
          </TableContainer>
        }
        onClose={() => setShowProductDetails(null)}
      />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
        onConfirm={handleConfirmation}
        isLoading={state?.isLoading}
        title="ATTENZIONE"
        text={
          tableData?.length !== acceptationData?.products?.length ? (
            <>
              Hai caricato solamente
              <br />
              <b>
                {tableData?.length} di {acceptationData?.products?.length}{" "}
                PRODOTTI
              </b>
              <br />
              presenti nel documento.
              <br />
              <br />
              <b>SEI SICURO DI VOLER PROCEDERE?</b>
              <br />
              L’operazione verrà conclusa e non sarà più possibile aggiungere le
              altre matricole
            </>
          ) : undefined
        }
      />
    </PageContainer>
  );
};

export default Acceptance;
